import { ApiResponse } from './types';

export enum ChatConversationType {
  Group = 'Group',
  Facility = 'Facility',
  Support = 'Support',
}

export enum ChatMessageType {
  User = 'User',
}

export interface ChatMessageModel {
  id: Guid;
  conversationId: Guid;
  clientMessageId: Guid;
  senderUserId: Guid;
  messageType: ChatMessageType;
  body: string | null;
  photoId: Guid | null;
  photoImageUrl: string | null;
  photoThumbnailUrl: string | null;
  replyToMessageId: Guid | null;
  replyTo: ChatReplyPreviewModel | null;
  sentDate: string;
  isDeleted: boolean;
  dateEdited: string | null;
  reactions: ChatMessageReactionModel[];
  isPending?: boolean;
}

export interface ChatMessageReactionModel {
  userId: Guid;
  reaction: string;
  reactedDate: string;
}

export interface ChatReactionUpdateModel {
  conversationId: Guid;
  messageId: Guid;
  userId: Guid;
  reaction: string | null;
  reactedDate: string | null;
}

export interface ChatReplyPreviewModel {
  messageId: Guid;
  senderUserId: Guid;
  body: string | null;
  hasPhoto: boolean;
  isDeleted: boolean;
}

export interface ChatConversationMemberModel {
  userId: Guid;
  dateLeft: string | null;
  lastReadDate: string | null;
  dateRead: string | null;
  dateMuted: string | null;
}

export interface ChatConversationModel {
  id: Guid;
  conversationType: ChatConversationType;
  name: string | null;
  members: ChatConversationMemberModel[];
  lastMessage: ChatMessageModel | null;
  lastActivityDate: string;
  photoId: Guid | null;
  photoImageUrl: string | null;
  photoThumbnailUrl: string | null;
}

export interface ChatReadModel {
  conversationId: Guid;
  userId: Guid;
  lastReadDate: string;
  dateRead: string;
}

export interface GetChatConversationsResponse extends ApiResponse {
  conversations: ChatConversationModel[];
}

export interface GetChatMessagesResponse extends ApiResponse {
  chatMessages: ChatMessageModel[];
  hasMore: boolean;
}

export interface ChatMessageReaderModel {
  userId: Guid;
  dateRead: string;
}

export interface GetChatMessageReadersResponse extends ApiResponse {
  readers: ChatMessageReaderModel[];
}

export interface StoreChatConversationRequest {
  conversationType: ChatConversationType;
  name: string | null;
  memberUserIds: Guid[];
}

export interface StoreChatConversationResponse extends ApiResponse {
  conversation: ChatConversationModel;
}

export interface StoreChatMessageRequest {
  clientMessageId: Guid;
  body: string;
  photoId: Guid | null;
  replyToMessageId: Guid | null;
}

export interface StoreChatMessageReactionRequest {
  reaction: string | null;
}

export interface StoreChatMessageReactionResponse extends ApiResponse {
  update: ChatReactionUpdateModel;
  memberUserIds: Guid[];
}

export interface StoreChatMessageResponse extends ApiResponse {
  message: ChatMessageModel;
  memberUserIds: Guid[];
}

export interface StoreChatConversationNameRequest {
  name: string | null;
}

export interface StoreChatConversationNameResponse extends ApiResponse {
  conversation: ChatConversationModel;
  memberUserIds: Guid[];
}

export interface StoreChatConversationPhotoRequest {
  photoId: Guid | null;
}

export interface StoreChatConversationPhotoResponse extends ApiResponse {
  conversation: ChatConversationModel;
  memberUserIds: Guid[];
}

export interface StoreChatMembersRequest {
  addUserIds: Guid[];
  removeUserIds: Guid[];
}

export interface StoreChatMembersResponse extends ApiResponse {
  conversation: ChatConversationModel | null;
  notifyUserIds: Guid[];
  conversationDeleted: boolean;
}

export interface StoreChatLastReadRequest {
  lastReadMessageId: Guid;
}

export type StoreChatLastReadResponse = ApiResponse;

export interface StoreChatMutedRequest {
  isMuted: boolean;
}

export type StoreChatMutedResponse = ApiResponse;

export interface DeleteChatMessageResponse extends ApiResponse {
  message: ChatMessageModel;
  memberUserIds: Guid[];
}

export interface AdminChatStatusModel {
  isEnabled: boolean;
  isTeamMember: boolean;
  canViewAllConversations: boolean;
  isSilenceEnabled: boolean;
  isTeamChatPinned: boolean;
}

export interface GetAdminChatStatusResponse extends ApiResponse, AdminChatStatusModel {}

export interface ChatPersonModel {
  userId: Guid;
  isActive: boolean;
  isSupport: boolean;
  firstName: string;
  lastName: string;
  displayName: string;
  profilePicture: string;
}

export interface GetChatPeopleResponse extends ApiResponse {
  people: ChatPersonModel[];
}

export interface GetChatUnreadFacilitiesResponse extends ApiResponse {
  facilityIds: Guid[];
}

export interface GetPresignedUriRequest {
  id: Guid;
  uriType?: UriType;
}

export interface PresignedUri {
  id: string;
  imageUri: string;
  thumbnailUri: string;
}

export enum UriType {
  Read = 'read',
  Write = 'write',
}
